import { Client, User } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { initMessagesListener } from "./listeners/messagesListener";
import { initParticipantsListener } from "./listeners/participantsListener";
import { initConversationListener } from "./listeners/conversationListener";
import { EngagementPhase } from "../definitions";
import { ConfigState } from "../../definitions";
import { initClientListeners } from "./listeners/clientListener";
import { notifications } from "../../notifications";
import { ACTION_START_SESSION, ACTION_LOAD_CONFIG } from "./actionTypes";
import { addNotification, changeEngagementPhase } from "./genericActions";
import { sessionDataHandler } from "../../sessionDataHandler";
import { MESSAGES_LOAD_COUNT } from "../../constants";
import { getStoredParticipantNames, storeParticipantName } from "../../participantStorage";

export function initConfig(config: ConfigState) {
    return {
        type: ACTION_LOAD_CONFIG,
        payload: config
    };
}

export function initSession({ token, conversationSid }: { token: string; conversationSid: string }) {
    return async (dispatch: Dispatch) => {
        let conversationsClient: Client;
        let conversation;
        let participants;
        let users;
        let messages;

        try {
            conversationsClient = await Client.create(token);

            if (conversationsClient.connectionState === "denied") {
                log.debug("client connection denied — clearing session data");
                sessionDataHandler.clear();
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                return;
            }
            try {
                conversation = await conversationsClient.getConversationBySid(conversationSid);
            } catch (e) {
                log.error("failed to get conversation by sid");
                if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 403) {
                    log.debug("forbidden — clearing session data");
                    sessionDataHandler.clear();
                    dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                    return;
                }
                dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                return;
            }

            participants = await conversation.getParticipants();
            users = await Promise.all(participants.map(async (p) => p.getUser()));
            users.forEach((u) => storeParticipantName(u.identity, u.friendlyName));

            /*
             * Rehydrate names for participants who have already left the conversation
             * (e.g. a Flex agent who transferred to another agent before this page load).
             */
            const liveIdentities = new Set(users.map((u) => u.identity));
            const historicalUsers = Object.entries(getStoredParticipantNames())
                .filter(([identity]) => !liveIdentities.has(identity))
                .map(([identity, friendlyName]) => ({ identity, friendlyName } as unknown as User));
            users = [...users, ...historicalUsers];

            messages = (await conversation.getMessages(MESSAGES_LOAD_COUNT)).items;
        } catch (e) {
            log.error("Something went wrong when initializing session", e);
            throw e;
        }

        dispatch({
            type: ACTION_START_SESSION,
            payload: {
                token,
                conversationSid,
                conversationsClient,
                conversation,
                users,
                participants,
                messages,
                conversationState: conversation.state?.current,
                currentPhase: EngagementPhase.MessagingCanvas
            }
        });

        initClientListeners(conversationsClient, dispatch);
        initConversationListener(conversation, dispatch);
        initMessagesListener(conversation, dispatch);
        initParticipantsListener(conversation, dispatch);
    };
}
